import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  IconButton,
  Container,
} from "@mui/material";
import { createAlert } from "@/features/homepage/alertSliceList";
import { 
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon 
} from "@mui/icons-material";
import {
  selectEditConnectionConnectionPairWorkflow,
  fetchOperations,
  fetchConnectionProfiles,
  fetchEndpoints,
  fetchConnectionIds,
  fetchConnectionAllPairs,
  selectEditConnectionOpen,
  selectEditSchemaOpen,
  setEditConnectionOpen,
  setEditSchemaOpen,
  setEditConnectionId,
  selectSelectedConnection,
  selectMapTabSelected,
  setMapTabSelected,
  selectConnectionSummaryRecords,
  selectEditConnectionId,
  setSchemaCollectionAdded,
  selectTransactionSettingsDataChangeDetected,
  selectCatalogSharingSetupDetails,
  selectConnectionPairObject,
} from "@/features/connections/integrationSlice";
import { ShepTab, ShepTabs } from "@/components/GenericComponents/ShepTab";
import Schema from "@/components/Integrations/ManageConnection/Schema";
import ConnectionPair from "@/components/Integrations/ManageConnection/ConnectionPair";
import Workflows from "@/components/Integrations/ManageConnection/Workflows";
import { useDispatch, useSelector } from "react-redux";
import {
  setIsMappingClicked,
  selectIsMappingClicked,
  selectSelectedSchema,
} from "@/features/mapSchema/mapSchemaSlice";
import Connection from "@/components/Integrations/ManageConnection/Connection";
import {
  handleEditConnection,
  validateForm,
  getEndpointSchema,
} from "@/components/Integrations/ManageConnection/ManageConnectionHelper";
import {
  ManageConnectionPopupProvider,
  FooterBarHost,
  FooterButton,
  SubtabBarHost,
  useManageConnectionFooter,
} from "@/components/Integrations/ManageConnection/ManageConnectionPopupContext";

const EditConnectionPopupInner = ({ initialTab }) => {
  const dispatch = useDispatch();

  //this info contains the updated row info.
  const rows = useSelector(selectConnectionSummaryRecords);
  const id = useSelector(selectEditConnectionId);

  useEffect(() => {
    dispatch(fetchConnectionIds());
    dispatch(fetchEndpoints());
    dispatch(fetchConnectionAllPairs());
    dispatch(fetchConnectionProfiles());
    dispatch(fetchOperations());
  }, [dispatch]);

  //Variables for multiple tabs
  const connectionId = useSelector(selectEditConnectionId);

  const [showErrors, setShowErrors] = useState(false);
  const [tabErrors, setTabErrors] = useState({
    connectionDetails: false,
    endpoints: false,
    schema: false,
    connectionPair: false,
    workflows: false,
  }); // State to track validation errors for each tab

  const currentConnection = useSelector(selectSelectedConnection);

  const jsonProperties = currentConnection.JSONProperties
    ? JSON.parse(currentConnection.JSONProperties)
    : {};

  //States for Connection Details tab
  const [connectionDetails, setConnectionDetails] = useState({
    connectionId: connectionId,
    connectionName: currentConnection.ConnectionName,
    displayName: currentConnection.DisplayName,
    connectionType: currentConnection.ConnectionType,
    shortForm: jsonProperties.ShortFormId,
    isDisabled: currentConnection.IsDisabled,
  });

  const [connectionTabValue, setConnectionTabValue] = useState(0);

  //Variables for Endpoints Screen
  const [endpointId, setEndpointId] = useState(currentConnection.EndpointId);

  //Variables for Connection Pair
  const [modifiedConnectionPairs, setModifiedConnectionPairs] = useState([]);

  const isEditConnectionOpen = useSelector(selectEditConnectionOpen);
  const isEditSchemaOpen = useSelector(selectEditSchemaOpen);

  const workflowModel = useSelector(selectEditConnectionConnectionPairWorkflow);
  const catalogSharingSetupDetails = useSelector(
    selectCatalogSharingSetupDetails
  );
  const connectionPairObject = useSelector(selectConnectionPairObject);
  const transactionSettingsDataChangeDetected = useSelector(
    selectTransactionSettingsDataChangeDetected
  );

  const open = isEditConnectionOpen || isEditSchemaOpen;

  const handleCloseDialogue = () => {
    dispatch(setEditConnectionOpen(false));
    dispatch(setEditSchemaOpen(false));
    dispatch(setEditConnectionId(false));
    dispatch(setMapTabSelected(false));
    dispatch(
      setSchemaCollectionAdded({
        schemaCollectionAdded: [],
      })
    );
  };
  const onClose = () => {
    if (transactionSettingsDataChangeDetected) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          message:
            "There are unsaved changes remaining, do you wish to close the window?",
          buttonLabel: "Close",
          onButtonClick: () => handleCloseDialogue(),
          buttonLabel2: "Cancel",
        })
      );
    } else {
      handleCloseDialogue();
    }
  };
  const [tabValue, setTabValue] = useState(initialTab);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const changeTab = (newValue) => {
    setTabValue(newValue);
    dispatch(setMapTabSelected(false));
  };
  const handleTabChange = (event, newValue) => {
    if (transactionSettingsDataChangeDetected) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          message:
            "There are unsaved changes remaining, do you wish to close the window?",
          buttonLabel: "Close",
          onButtonClick: () => changeTab(newValue),
          buttonLabel2: "Cancel",
        })
      );
    } else {
      changeTab(newValue);
    }
  };

  const handleClose = () => {
    setConfirmationOpen(false);
  };

  const saveButtonClicked = async () => {
    if (
      !validateForm(
        tabErrors,
        setTabValue,
        dispatch,
        setShowErrors,
        setConnectionTabValue
      )
    ) {
      return; // Exit if validation fails
    }

    await handleEditConnection(
      dispatch,
      connectionId,
      connectionDetails,
      modifiedConnectionPairs,
      catalogSharingSetupDetails,
      connectionPairObject,
      workflowModel,
      endpointId,
      onClose
    );
  };

  useEffect(() => {
    getEndpointSchema(dispatch, connectionId);
  }, [connectionId]);

  const isMapTabSelected = useSelector(selectMapTabSelected);
  const selectedSchema = useSelector(selectSelectedSchema);

  const handleMapping = () => {
    if (selectedSchema === undefined || selectedSchema === null) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          message: "A record was not selected to do mapping!",
          buttonLabel: "Close",
        })
      );
      dispatch(setIsMappingClicked({ isMappingClicked: false }));
    } else {
      dispatch(setIsMappingClicked({ isMappingClicked: true }));
    }
  };

  const isMappingClicked = useSelector(selectIsMappingClicked);

  const { registerScrollContainer } = useManageConnectionFooter();
  const containerScrollRef = useCallback(
    (node) => {
      if (node) {
        registerScrollContainer(node);
      }
    },
    [registerScrollContainer]
  );

  return (
    <div>
      {/* Footer button rendered via portal to FooterBarHost */}
      <FooterButton
        variant="primary"
        onClick={saveButtonClicked}
        hidden={tabValue === 1}
      >
        Save
      </FooterButton>

      <Dialog
        className="connection-settings-container"
        open={open}
        onClose={onClose}
        PaperProps={{
          style: {
            backgroundColor: "transparent",
            boxShadow: "none",
            minWidth: "95vw",
            minHeight: "99vh",
          },
        }}
        sx={{ zIndex: "1500" }}
      >
        <DialogTitle
          className="workflow-settings-heading"
          style={{
            display: "flex",
            justifyContent: "space-between",
            backgroundColor: "var(--primary-main)",
            color: "var(--default-light)",
            fontSize: "24px",
            borderRadius: "15px 15px 0 0",
          }}
        >
          <div style={{ display: "flex" }}>
            {tabValue === 1 ? (
              <div style={{ fontWeight: "var(--font-semi-bold)" }}>
                Edit Schema
              </div>
            ) : (
              <div style={{ fontWeight: "var(--font-semi-bold)" }}>
                Edit Connection
              </div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <IconButton
              className="closeIconButton"
              style={{ color: "var(--default-light)" }}
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent
          className="dialogContent"
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            padding: "10px",
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              boxShadow: "0px 1.5px 0px var(--primary-main)",
              backgroundColor: "var(--disabled)",
              pt: 1,
            }}
          >
            <ShepTabs
              variant="primary"
              value={tabValue}
              onChange={handleTabChange}
            >
              <ShepTab variant="primary" key="0" label={"Connection"} />
              <ShepTab variant="primary" key="1" label={"Schema"} />
              <ShepTab variant="primary" key="2" label={"Connection Pair"} />
              <ShepTab variant="primary" key="3" label={"Workflows"} />
            </ShepTabs>
          </Box>
          <SubtabBarHost />

          <Container
            sx={{
              flex: "1 1 auto",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              pt: "10px",
            }}
            ref={containerScrollRef}
          >
            <Box sx={{ flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column" }}>
              {tabValue === 0 && (
                <Connection
                  connectionDetails={connectionDetails}
                  setConnectionDetails={setConnectionDetails}
                  showErrors={showErrors}
                  setTabErrors={setTabErrors}
                  connectionId={connectionId}
                  selectedEndpointId={endpointId}
                  setSelectedEndpointId={setEndpointId}
                  tabValue={connectionTabValue}
                  setTabValue={setConnectionTabValue}
                />
              )}
              {tabValue === 1 && <Schema />}
              {tabValue === 2 && (
                <ConnectionPair
                  modifiedConnectionPairs={modifiedConnectionPairs}
                  setModifiedConnectionPairs={setModifiedConnectionPairs}
                  setHasErrors={(hasErrors) =>
                    setTabErrors((prev) => ({
                      ...prev,
                      connectionPair: hasErrors,
                    }))
                  }
                  showErrors={showErrors}
                />
              )}
              {tabValue === 3 && <Workflows connectionId={connectionId} />}
            </Box>
          </Container>
          <FooterBarHost />
        </DialogContent>
      </Dialog>
      <Dialog
        open={confirmationOpen}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxWidth: "1200px",
            width: "800px",
            height: "400px",
            borderRadius: "15px",
          },
        }}
        sx={{ zIndex: "1510" }}
      >
        <DialogContent style={{ textAlign: "center", paddingTop: "60px" }}>
          <DialogContentText id="alert-dialog-description">
            <CheckCircleIcon
              sx={{
                alignSelf: "center",
                color: "#4caf50",
                marginLeft: "auto",
                width: 60,
                height: 60,
              }}
            />
            <h3>We Received Your Request!</h3>
            Our team will contact you soon to validate your request.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <div style={{ textAlign: "center", paddingBottom: "60px" }}>
            <Button className="btn-primary" onClick={handleClose} autoFocus>
              Close
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </div>
  );
};

// Wrapper component to provide context
const EditConnectionPopup = (props) => (
  <ManageConnectionPopupProvider>
    <EditConnectionPopupInner {...props} />
  </ManageConnectionPopupProvider>
);

export default EditConnectionPopup;
